import { cn } from '@pubstack/ui/utils/cn'
import { keyframes } from '@emotion/react'
import styled from '@emotion/styled'
import { Fragment, FunctionComponent, PropsWithChildren, useEffect, useState } from 'react'
import { Color, Colors, ColorUtil } from '~/assets/style/colors'
import { Fonts } from '~/assets/style/fonts'
import { ElevationLevel } from '~/assets/style/tokens'
import { ScrollbarStyle } from '~/assets/style/utils'
import { TrackingLabel } from '~/components/layout/ProductTrackingIntegration'
import { WithClassName } from '~/types/utils'
import Button from './Button'

export const FLYOUT_PORTAL_ID = 'flyout-portal'

type WidthPx = `${number}px`
const flyoutEase = 'cubic-bezier(0.695, 0.0485, 0.34, 1)'

const flyoutIn = keyframes`
  0% {
    opacity: 0;
    transform: translateX(100%);
  }
  75% {
    opacity: 1;
    transform: translateX(0);
  }
`

const flyoutOut = keyframes`
  0% {
    opacity: 1;
    transform: translateX(0);
  }
  85% {
    opacity: 0;
  }
  100% {
    opacity: 0;
    transform: translateX(100%);
  }
`

const flyoutFadeIn = keyframes`
  from {
    background-color: transparent;
  }
  to {
    background-color: ${ColorUtil.hexOpacity(Colors.Jet, 0.1)};
  }
`

const flyoutFadeOut = keyframes`
  from {
    background-color: ${ColorUtil.hexOpacity(Colors.Jet, 0.1)};
  }
  to {
    background-color: transparent;
  }
`

const FlyoutRoot = styled.div`
  display: flex;
  justify-content: flex-end;
  right: 0;
    
  width: 100%;
  height: 100%;
    
  position: absolute;
  overflow-x: hidden;
  overflow-y: visible;

  ${ElevationLevel.middle}

  &.flyout-open {
    animation: 0.15s ${flyoutEase} ${flyoutFadeIn} normal forwards;
    > * {
      animation: 0.25s ${flyoutEase} ${flyoutIn} normal forwards;
    }
  }
  &.flyout-close {
    animation: 0.1s ${flyoutEase} ${flyoutFadeOut} normal forwards;
    > * {
      animation: 0.2s ${flyoutEase} ${flyoutOut} normal forwards;
    }
  }
`

const FlyoutWrapper = styled.div<{ color?: Color; width?: WidthPx }>`
  display: grid;
  grid-template-areas: 
          'title title headerClose'
          'body body body'
          'actionClose actions actions';

  gap: 12px;
  grid-template-rows: auto 1fr auto;
  grid-template-columns: auto 1fr auto;
    
  background-color: ${Colors.White};
  width: ${({ width }) => width};
  position: relative;
  box-shadow: 0 0 20px rgba(54, 54, 54, 0.5);

  border-left: 6px solid;
  border-image-slice: 1;
  border-image-source: ${({ color }) => `linear-gradient(180deg, ${color ?? Colors.King} 0%, ${color ?? Colors.Pool} 100%)`};

  padding: 20px 20px 12px;
`

const FlyoutBody = styled.div`
  grid-area: body;
  ${ScrollbarStyle};
  flex: 1;
  overflow-y: auto;
  padding-top: 8px;

  /* This region scrolls, which browsers make focusable in its own right, and the steppers focus it on open
     so Enter can finish a step. Neither is the user asking to be shown where the keyboard is, and the ring
     it draws reads as a stray rectangle around the flyout's whole middle. */
  &:focus,
  &:focus-visible {
    outline: none;
  }
`

const FlyoutActions = styled.div`
  grid-area: actions;
  display: flex;
  align-items: center;
  gap: 12px;
  justify-content: flex-end;
  height: 46px;
`
const FlyoutTitle = styled.div`
  grid-area: title;
  ${Fonts.H1}
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  align-content: center;
`

const HeaderClose = styled(Button)`
  grid-area: headerClose;
  justify-self: end;
`
const ActionClose = styled(Button)`
    grid-area: actionClose;
    margin: 4px 0;
    padding: 0 12px;
    ${Fonts.P1};
`

export type FlyoutProps = WithClassName & {
  color?: Color
  isOpen: boolean
  onRequestClose: () => unknown
  width?: WidthPx
  closeTrackingLabel: TrackingLabel
}
const BaseContainer: FunctionComponent<PropsWithChildren<FlyoutProps>> = ({ className, color, children, isOpen, onRequestClose, width = '600px', closeTrackingLabel }) => {
  const [isOpenInternal, setIsOpenInternal] = useState(isOpen)
  useEffect(() => {
    if (isOpen !== isOpenInternal) {
      if (isOpenInternal) {
        const timeout = setTimeout(() => setIsOpenInternal(isOpen), 200)
        return () => clearTimeout(timeout)
      } else {
        // oxlint-disable-next-line react/set-state-in-effect -- opening is immediate while closing waits for the 200ms exit animation above; both branches belong to the same timer-driven effect
        setIsOpenInternal(isOpen)
      }
    }
  }, [isOpen])

  /**
   * Escape closes the flyout, the way it closes every overlay a reader has met before.
   *
   * Through `onRequestClose` rather than straight to the handler, so the key is the same act as the
   * close button and the click on the backdrop: whatever guard the open component registered still gets
   * asked about unsaved changes, and the close is still tracked.
   *
   * Bound to the document rather than to the flyout, because the flyout is not focused when it opens —
   * a key handler on the element itself would only work once something inside it had been clicked. It is
   * listening only while open, and `isOpen` rather than `isOpenInternal` is the condition: the extra
   * 200ms of exit animation is not a window in which Escape should close anything a second time.
   */
  useEffect(() => {
    if (!isOpen) return
    const onKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.key !== 'Escape') return
      /* A modal opened from the flyout is the topmost thing on screen, and react-modal already closes it
         on Escape. One press should dismiss one thing: without this, confirming a save and changing your
         mind would take the confirmation *and* the flyout behind it, losing whatever was typed into it.
         The overlay stays in the DOM through react-modal's own exit animation, so this holds until it is
         really gone. */
      if (document.querySelector('.ReactModal__Overlay')) return
      onRequestClose()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [isOpen, onRequestClose])

  return (
    isOpenInternal && (
      <FlyoutRoot onClick={onRequestClose} className={cn([className, isOpen ? 'flyout-open' : 'flyout-close'])}>
        <FlyoutWrapper onClick={(e) => e.stopPropagation()} color={color} width={width}>
          <HeaderClose variant={'tertiary'} onClick={onRequestClose} iconName={'close'} trackingLabel={closeTrackingLabel} />
          <ActionClose variant={'tertiary'} onClick={onRequestClose} size={'s'} trackingLabel={closeTrackingLabel}>
            Close
          </ActionClose>
          {children}
        </FlyoutWrapper>
      </FlyoutRoot>
    )
  )
}

const Container = styled(BaseContainer)``

export const Flyout = {
  Content: Fragment,
  Title: FlyoutTitle,
  Body: FlyoutBody,
  Actions: FlyoutActions,
  Container,
}
